'use client'

import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import primaryLogo from '@/../public/brand/teli-logo-primary.png'
import lightLogo from '@/../public/brand/teli-logo-light.png'

interface LogoProps {
  className?: string
  /** Logo image height in px; the "Hub" wordmark scales with it. */
  height?: number
  priority?: boolean
  /** Custom click handler (e.g. useRoleRedirect's handleLogoClick). */
  onClick?: () => void
  /** Set false on public/candidate pages where the logo must not navigate. */
  clickable?: boolean
  /** Logo only, without the Hub wordmark (candidate-facing pages). */
  markOnly?: boolean
}

/**
 * Teli Hub product mark: the Teli logo (primary ink on light, light ink on
 * dark, switched by CSS off the html.dark class) followed by "Hub" in the
 * display font. Clicking goes to the dashboard unless a custom onClick is
 * provided or `clickable={false}`.
 */
export function Logo({
  className,
  height = 28,
  priority = false,
  onClick,
  clickable = true,
  markOnly = false,
}: LogoProps) {
  const router = useRouter()
  const width = Math.round(height * (primaryLogo.width / primaryLogo.height))

  const logoElement = (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <Image
        src={primaryLogo}
        alt="Teli"
        width={width}
        height={height}
        priority={priority}
        className="object-contain dark:hidden"
        style={{ height, width: 'auto' }}
      />
      <Image
        src={lightLogo}
        alt="Teli"
        width={width}
        height={height}
        priority={priority}
        className="object-contain hidden dark:block"
        style={{ height, width: 'auto' }}
      />
      {!markOnly && (
        <span
          className="font-display font-semibold text-gray-900 dark:text-gray-50 leading-none"
          style={{ fontSize: Math.round(height * 0.72) }}
        >
          Hub
        </span>
      )}
    </span>
  )

  if (!clickable && !onClick) {
    return logoElement
  }

  return (
    <button
      type="button"
      onClick={onClick ?? (() => router.push('/'))}
      className="cursor-pointer hover:opacity-80 transition-opacity"
      aria-label="Go to dashboard"
    >
      {logoElement}
    </button>
  )
}
